import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface StreamingState {
  isStreaming: boolean;
  content: string;
  isComplete: boolean;
  error: string | null;
}

export interface VisualOptions {
  style: string;
  count: number | "auto";
  useFontAwesome: boolean;
  mobilePreview: boolean;
}

export interface WritingConstraints {
  preserveCitations: boolean;
  maintainFormalVoice: boolean;
  keepStandardCapitalization: boolean;
  avoidSlang: boolean;
  maintainStructure: boolean;
}

export interface WritingStyleConfig {
  style: string;
  constraints: WritingConstraints;
  customGuidelines?: string;
}

export const useStreamingHumanizer = () => {
  const [streamingState, setStreamingState] = useState<StreamingState>({
    isStreaming: false,
    content: '',
    isComplete: false,
    error: null,
  });

  const streamHumanizeText = useCallback(async (
    text: string,
    includeVisuals: boolean = false,
    visualOptions?: VisualOptions,
    intensity?: string,
    format?: string
  ) => {
    setStreamingState({
      isStreaming: true,
      content: '',
      isComplete: false,
      error: null,
    });

    try {
      const session = (await supabase.auth.getSession()).data.session;
      const accessToken = session?.access_token;
      if (!accessToken) {
        throw new Error('You must be signed in to use streaming');
      }

      // The edge function returns a streaming response
      // We need to handle the ReadableStream response
      const response = await fetch('https://zctacexxhzjuntlnltqa.supabase.co/functions/v1/humanize-text-stream', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          text,
          includeVisuals,
          visualOptions,
          intensity,
          format
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          while (true) {
            const lineEnd = buffer.indexOf('\n\n');
            if (lineEnd === -1) break;

            const chunk = buffer.slice(0, lineEnd).trim();
            buffer = buffer.slice(lineEnd + 2);

            if (chunk.startsWith('data: ')) {
              const data = chunk.slice(6);
              try {
                const parsed = JSON.parse(data);
                
                if (parsed.type === 'delta') {
                  setStreamingState(prev => ({
                    ...prev,
                    content: prev.content + (parsed.content ?? ''),
                  }));
                } else if (parsed.type === 'done') {
                  setStreamingState(prev => ({
                    ...prev,
                    content: parsed.content,
                    isComplete: true,
                    isStreaming: false,
                  }));
                  
                  // Dispatch credits update event
                  if (parsed.credits_remaining !== undefined) {
                    window.dispatchEvent(new CustomEvent('creditsUpdated', {
                      detail: { credits_remaining: parsed.credits_remaining }
                    }));
                  }
                  
                  return parsed.content;
                } else if (parsed.type === 'error') {
                  throw new Error(parsed.error);
                }
              } catch (e) {
                console.error('Error parsing stream data:', e);
              }
            }
          }
        }
      } finally {
        reader.cancel();
      }
    } catch (error) {
      console.error('Streaming error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to stream text';
      
      setStreamingState(prev => ({
        ...prev,
        error: errorMessage,
        isStreaming: false,
      }));

      if (errorMessage.includes('No active subscription') || errorMessage.includes('No credits remaining')) {
        toast.error(errorMessage);
      } else {
        toast.error(errorMessage);
      }
      
      throw error;
    }
  }, []);

  const reset = useCallback(() => {
    setStreamingState({
      isStreaming: false,
      content: '',
      isComplete: false,
      error: null,
    });
  }, []);

  return {
    streamingState,
    streamHumanizeText,
    reset,
  };
};